import React from 'react';
import { TrendingUp, Layers, PieChart, Target, Users } from 'lucide-react';
import type {
  CompletionTrend,
  CategoryBreakdownEntry,
  CertificationDistribution,
  SkillAssessment,
  RoleDistributionEntry,
} from '@/services/PagesServices/AnalyticsService';
import { describeArc } from '../utils';

type DonutSegment = CertificationDistribution & { startAngle: number; endAngle: number };

type RoleDonutSegment = RoleDistributionEntry & { percentage: number; startAngle: number; endAngle: number };

interface OverviewChartsExtraProps {
  completionTrends: CompletionTrend[];
  categoryBreakdown: CategoryBreakdownEntry[];
  certDistribution: CertificationDistribution[];
  donutData: DonutSegment[];
  skillsAssessment: SkillAssessment[];
  roleDistribution: RoleDistributionEntry[];
  roleDonutData: RoleDonutSegment[];
}

const OverviewChartsExtra: React.FC<OverviewChartsExtraProps> = ({
  completionTrends, categoryBreakdown, certDistribution,
  donutData, skillsAssessment, roleDistribution, roleDonutData,
}) => {
  return (
    <>
      {/* Completion Trends + Category Breakdown */}
      <div className="analytics-grid-2">
        {completionTrends.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <TrendingUp size={20} className="analytics-section-icon" />
              Completion Trends
            </div>
            <div className="analytics-section-subtitle">Monthly enrollment vs completion</div>
            <div className="analytics-trends-chart">
              {completionTrends.map((trend, i) => {
                const max = Math.max(...completionTrends.map(t => Math.max(t.enrolled, t.completed)), 1);
                const enrollPct = (trend.enrolled / max) * 100;
                const completePct = (trend.completed / max) * 100;
                return (
                  <div key={i} className="analytics-trend-bar-group">
                    <div className="analytics-trend-bars">
                      <div className="analytics-trend-bar enrolled" style={{ height: `${enrollPct}%` }}
                        title={`Enrolled: ${trend.enrolled}`}>
                        <span className="analytics-trend-bar-value">{trend.enrolled}</span>
                      </div>
                      <div className="analytics-trend-bar completed" style={{ height: `${completePct}%` }}
                        title={`Completed: ${trend.completed}`}>
                        <span className="analytics-trend-bar-value">{trend.completed}</span>
                      </div>
                    </div>
                    <div className="analytics-trend-label">{trend.month}</div>
                  </div>
                );
              })}
            </div>
            <div className="analytics-trend-legend">
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: 'var(--color-primary)' }} />
                Enrolled
              </div>
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: '#16a34a' }} />
                Completed
              </div>
            </div>
          </div>
        )}

        {categoryBreakdown.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Layers size={20} className="analytics-section-icon" />
              Course Category Breakdown
            </div>
            <div className="analytics-section-subtitle">Enrollment and performance by category</div>
            <div className="analytics-category-breakdown-list">
              {categoryBreakdown.map((cat, i) => {
                const maxEnrollments = Math.max(...categoryBreakdown.map(c => c.enrollments), 1);
                const barPct = (cat.enrollments / maxEnrollments) * 100;
                return (
                  <div key={i} className="analytics-category-row">
                    <div className="analytics-category-row-label">
                      <div className="analytics-category-dot" style={{ background: cat.color }} />
                      <span className="analytics-category-name">{cat.category}</span>
                    </div>
                    <div className="analytics-category-row-bar">
                      <div className="analytics-category-bar-bg">
                        <div className="analytics-category-bar-fill"
                          style={{ width: `${barPct}%`, background: cat.color }} />
                      </div>
                    </div>
                    <div className="analytics-category-row-stats">
                      <span className="analytics-category-enrollment-count">{cat.enrollments}</span>
                      <span className={`analytics-category-completion ${cat.completionRate >= 70 ? 'high' : cat.completionRate >= 40 ? 'medium' : 'low'}`}>
                        {cat.completionRate}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="analytics-category-breakdown-footer">
              <span>Enrollments</span>
              <span>Completion Rate</span>
            </div>
          </div>
        )}
      </div>

      {/* Certifications + Skills + Role Distribution */}
      <div className="analytics-grid-2">
        <div className="analytics-stacked-column">
          {certDistribution.length > 0 && (
            <div className="analytics-section" style={{ marginBottom: 0 }}>
              <div className="analytics-section-title">
                <PieChart size={20} className="analytics-section-icon" />
                Certification Distribution
              </div>
              <div className="analytics-cert-donut-row">
                <svg viewBox="0 0 120 120" className="analytics-donut-svg">
                  {donutData.map((seg, i) => (
                    <path key={i}
                      d={describeArc(60, 60, 45, seg.startAngle, seg.endAngle - 0.5)}
                      fill="none" stroke={seg.color || '#6366f1'} strokeWidth="18" strokeLinecap="round"
                    />
                  ))}
                  <text x="60" y="56" textAnchor="middle" fill="var(--color-black-text)"
                    fontSize="18" fontWeight="700">{certDistribution.length}</text>
                  <text x="60" y="72" textAnchor="middle" fill="var(--color-secondary-text)"
                    fontSize="10">Types</text>
                </svg>
                <div className="analytics-cert-legend">
                  {certDistribution.map((cert, i) => (
                    <div key={i} className="analytics-cert-legend-item">
                      <div className="analytics-cert-color" style={{ backgroundColor: cert.color }} />
                      <span className="analytics-cert-type">{cert.type}</span>
                      <span className="analytics-cert-pct">{cert.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {skillsAssessment.length > 0 && (
            <div className="analytics-section" style={{ marginBottom: 0 }}>
              <div className="analytics-section-title">
                <Target size={20} className="analytics-section-icon" />
                Skills Assessment
              </div>
              <div className="analytics-section-subtitle">Average scores across skill areas</div>
              <div className="analytics-skills-list">
                {skillsAssessment.map((skill, i) => (
                  <div key={i} className="analytics-skill-item">
                    <div className="analytics-skill-header">
                      <div>
                        <span className="analytics-skill-name">{skill.skill}</span>
                        <span className="analytics-skill-users"> · {skill.users} users</span>
                      </div>
                      <div className={`analytics-skill-score ${skill.score >= 70 ? 'high' : skill.score >= 40 ? 'medium' : 'low'}`}>
                        {skill.score}%
                      </div>
                    </div>
                    <div className="analytics-progress-bar">
                      <div className={`analytics-progress-fill ${skill.score >= 70 ? 'high' : skill.score >= 40 ? 'medium' : 'low'}`}
                        style={{ width: `${skill.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {roleDonutData.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Users size={20} className="analytics-section-icon" />
              User Role Distribution
            </div>
            <div className="analytics-section-subtitle">Breakdown of platform users by role</div>
            <div className="analytics-cert-donut-row" style={{ marginTop: 20 }}>
              <svg viewBox="0 0 140 140" className="analytics-donut-svg" style={{ width: 140, height: 140 }}>
                {roleDonutData.map((seg, i) => (
                  <path key={i}
                    d={describeArc(70, 70, 50, seg.startAngle, seg.endAngle - 0.5)}
                    fill="none" stroke={seg.color} strokeWidth="20" strokeLinecap="round"
                  />
                ))}
                <text x="70" y="66" textAnchor="middle" fill="var(--color-black-text)"
                  fontSize="20" fontWeight="700">
                  {roleDistribution.reduce((s, r) => s + r.count, 0)}
                </text>
                <text x="70" y="82" textAnchor="middle" fill="var(--color-secondary-text)"
                  fontSize="10">Total Users</text>
              </svg>
              <div className="analytics-cert-legend">
                {roleDonutData.map((role, i) => (
                  <div key={i} className="analytics-cert-legend-item">
                    <div className="analytics-cert-color" style={{ backgroundColor: role.color }} />
                    <span className="analytics-cert-type">{role.role}</span>
                    <span className="analytics-cert-pct">{role.count} ({role.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(OverviewChartsExtra);
